import { isNotAuditor } from "./teams";
import { useFabriqStore } from "@/store/fabriq";
import { useTeamsStore } from "@/store/teams";
import { Team } from "@/types";
import { computed } from "vue";
import {
  buildPermission,
  PermissionObject,
} from "@/composables/permissions/permissions";
import { useClientStore } from "@/store/client";

type TicketPermission = {
  shareTicket(params: {
    isCreatingTicket: true;
    teamId: number;
    ignoreTeamSharingSetting?: boolean;
  }): boolean;
};

export type TicketPermissions = PermissionObject<
  "ticket",
  TicketPermission,
  boolean
>;

export function useTicketPermissions(): PermissionObject<
  "ticket",
  TicketPermission,
  false
>;

export function useTicketPermissions(
  ticketIdForPermission: number
): PermissionObject<"ticket", TicketPermission, true>;

export function useTicketPermissions(
  ticketIdForPermission?: number
): PermissionObject<"ticket", TicketPermission, boolean> {
  const fabriqStore = useFabriqStore();

  const currentUserId = computed<number | undefined>(
    () => fabriqStore.user?.id
  );
  const teamsStore = useTeamsStore();
  const clientStore = useClientStore();

  function canShareTicket({
    teamId,
    ignoreTeamSharingSetting,
  }: {
    ticketId: undefined;
    teamId: number;
    ignoreTeamSharingSetting?: boolean;
  }) {
    const userId = currentUserId.value;
    const currentUser = fabriqStore.user;
    if (!userId || !currentUser) return false;
    const team: Team | undefined = teamsStore.collection.find(
      (t: Team) => t.id === teamId
    );
    if (!team) return false;
    if (currentUser.is_client_admin) return true;

    const teamSiteId = team.groups[0];
    const teamSite = clientStore.groups.find(
      (group) => group.id === teamSiteId
    );
    if (!teamSite) return false;

    if (teamSite.admins.includes(userId)) return true;

    if (ignoreTeamSharingSetting) return isNotAuditor(team, userId);

    const teamSharingSetting = team.sharingSetting;
    // fallback in case it is not loaded
    if (!teamSharingSetting) return isNotAuditor(team, userId);

    if (teamSharingSetting === "organization") return true;

    if (teamSharingSetting === "team") {
      return isNotAuditor(team, userId);
    }

    if (teamSharingSetting === "site") {
      const userTeams: Team[] = teamsStore.collection.filter((t: Team) =>
        t.users.includes(userId)
      );
      const userTeamsInSameSite = userTeams.filter(
        (t) => t.groups[0] === teamSiteId
      );
      return userTeamsInSameSite.some((t) => isNotAuditor(t, userId));
    }
    return false;
  }

  return buildPermission("ticket", Boolean(ticketIdForPermission), {
    shareTicket: ({ isCreatingTicket, teamId, ignoreTeamSharingSetting }) => {
      if (ticketIdForPermission || !isCreatingTicket) {
        throw new Error("This is not handled yet.");
      }
      const ticketId = undefined;
      return canShareTicket({ teamId, ticketId, ignoreTeamSharingSetting });
    },
  });
}
